import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * OFFLINE CACHE SERVICE — IndexedDB for Critical Hospital Data
 *
 * Stores billing snapshots, payment records, and discharge summaries
 * in IndexedDB so hospital staff can access them even when:
 *   - API server crashes
 *   - Internet goes down
 *   - Database is unreachable
 *
 * Syncs automatically every 3 minutes while app is running.
 * Data survives browser restarts, tab closures, and app crashes.
 */

export interface CachedBillingRecord {
  id: string;                    // "bill_{visitId}" or "ip_{admissionId}"
  visitId: number;
  admissionId?: number;
  patientName: string;
  patientPhone?: string;
  wardBed?: string;              // "Ward A - Bed 3"
  department?: string;
  doctorName?: string;
  type: 'OP' | 'IP';
  totalAmount: number;
  totalPaid: number;
  totalRefunded?: number;          // Sum of processed refunds (Phase 1 follow-up)
  totalPending: number;
  advancePaid?: number;
  status: 'Active' | 'Discharged' | 'Checked Out';
  billStatus?: string;           // "Paid" | "Pending" | "Partial"
  lineItems: any[];
  ipCharges?: any[];
  advances?: any[];
  checkInTime?: string;
  checkOutTime?: string;
  admissionDate?: string;
  dischargeDate?: string;
  lastSyncedAt: number;          // Timestamp
  visitCount?: number;           // Aggregate count when dedup'd in emergency view (optional; not persisted)
}

export interface CachedDischargeSummary {
  id: string;                    // "discharge_{visitId}"
  visitId: number;
  patientName: string;
  patientPhone?: string;
  patientAge?: string;
  gender?: string;
  department?: string;
  doctorName?: string;
  hospitalName?: string;
  type: 'OP' | 'IP';
  diagnosis?: string;
  prescriptions: any[];
  testResults: any[];
  vitals?: any;
  doctorNotes: any[];
  admission?: any;
  billing: {
    totalAmount: number;
    totalPaid: number;
    totalPending: number;
    lineItems: any[];
  };
  ipBillSummary?: any;
  checkInTime?: string;
  checkOutTime?: string;
  lastSyncedAt: number;
}

export interface CachedPaymentRecord {
  id: string;                    // "payment_{timestamp}_{billId}"
  visitId: number;
  patientName: string;
  amount: number;
  paymentMethod: string;
  paymentReference?: string;
  billType: string;              // "Lab", "Pharmacy", "OP", "IP Advance", "IP Charge"
  description: string;
  recordedAt: number;
  syncedToServer: boolean;
}

/**
 * Owner decision B7 (2026-11-01, DISC-038): how long a cached patient record may live on this device. Sign-in and every
 * sign-out already wipe the whole cache; this lifetime limits how much patient data piles up on disk during a long
 * signed-in shift (nothing older than a day is ever kept or shown). 24 h still covers a full-day internet outage for the
 * emergency offline view, because every bill / discharge summary is re-cached each time a screen opens it online.
 */
export const OFFLINE_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** The time a cached record was last refreshed: billing and discharge records carry lastSyncedAt, payments recordedAt. */
function cachedAt(record: any): number | undefined {
  const t = record?.lastSyncedAt ?? record?.recordedAt;
  return typeof t === 'number' && isFinite(t) ? t : undefined;
}

/** True when the record was cached within the lifetime. A record with no usable timestamp counts as stale (safer). */
export function isFreshCacheRecord(record: any, now: number = Date.now(), maxAgeMs: number = OFFLINE_CACHE_MAX_AGE_MS): boolean {
  const t = cachedAt(record);
  return t !== undefined && now - t <= maxAgeMs;
}

@Injectable({ providedIn: 'root' })
export class OfflineCacheService {

  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'MediTrackOfflineCache';
  private readonly DB_VERSION = 1;
  private syncInterval: any = null;

  // Stores
  private readonly BILLING_STORE = 'billing_records';
  private readonly DISCHARGE_STORE = 'discharge_summaries';
  private readonly PAYMENT_STORE = 'payment_records';

  // Observable for cache status
  private lastSyncTime$ = new BehaviorSubject<number>(0);
  lastSync$ = this.lastSyncTime$.asObservable();

  constructor() {
    // B7: once the cache is open, drop anything older than OFFLINE_CACHE_MAX_AGE_MS left from an earlier load.
    this.initDB().then(() => this.cleanupOldRecords()).catch(() => { /* cache unavailable — nothing to prune */ });
  }

  // ══════════════════════════════════════════════════════════
  //  DATABASE INITIALIZATION
  // ══════════════════════════════════════════════════════════

  private initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (this.db) { resolve(this.db); return; }

      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result as IDBDatabase;

        // Billing records store
        if (!db.objectStoreNames.contains(this.BILLING_STORE)) {
          const billingStore = db.createObjectStore(this.BILLING_STORE, { keyPath: 'id' });
          billingStore.createIndex('type', 'type', { unique: false });
          billingStore.createIndex('status', 'status', { unique: false });
          billingStore.createIndex('patientName', 'patientName', { unique: false });
        }

        // Discharge summaries store
        if (!db.objectStoreNames.contains(this.DISCHARGE_STORE)) {
          const dischargeStore = db.createObjectStore(this.DISCHARGE_STORE, { keyPath: 'id' });
          dischargeStore.createIndex('visitId', 'visitId', { unique: true });
          dischargeStore.createIndex('patientName', 'patientName', { unique: false });
        }

        // Payment records store (append-only log)
        if (!db.objectStoreNames.contains(this.PAYMENT_STORE)) {
          const paymentStore = db.createObjectStore(this.PAYMENT_STORE, { keyPath: 'id' });
          paymentStore.createIndex('visitId', 'visitId', { unique: false });
          paymentStore.createIndex('recordedAt', 'recordedAt', { unique: false });
        }
      };

      request.onsuccess = (event: any) => {
        this.db = event.target.result;
        resolve(this.db!);
      };

      request.onerror = () => {
        console.error('OfflineCache: Failed to open IndexedDB');
        reject(request.error);
      };
    });
  }

  private async getDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    return this.initDB();
  }

  // ══════════════════════════════════════════════════════════
  //  BILLING RECORDS — Active patient bills + payment status
  // ══════════════════════════════════════════════════════════

  async saveBillingRecord(record: CachedBillingRecord): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.BILLING_STORE, 'readwrite');
      tx.objectStore(this.BILLING_STORE).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async saveBillingRecords(records: CachedBillingRecord[]): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.BILLING_STORE, 'readwrite');
      const store = tx.objectStore(this.BILLING_STORE);
      records.forEach(r => store.put(r));
      tx.oncomplete = () => { this.lastSyncTime$.next(Date.now()); resolve(); };
      tx.onerror = () => reject(tx.error);
    });
  }

  async getAllBillingRecords(): Promise<CachedBillingRecord[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.BILLING_STORE, 'readonly');
      const request = tx.objectStore(this.BILLING_STORE).getAll();
      request.onsuccess = () => resolve(((request.result || []) as CachedBillingRecord[]).filter(r => isFreshCacheRecord(r)));
      request.onerror = () => reject(request.error);
    });
  }

  async getBillingRecord(id: string): Promise<CachedBillingRecord | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.BILLING_STORE, 'readonly');
      const request = tx.objectStore(this.BILLING_STORE).get(id);
      request.onsuccess = () => resolve(request.result && isFreshCacheRecord(request.result) ? request.result : null);
      request.onerror = () => reject(request.error);
    });
  }

  async removeBillingRecord(id: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.BILLING_STORE, 'readwrite');
      tx.objectStore(this.BILLING_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // ══════════════════════════════════════════════════════════
  //  DISCHARGE SUMMARIES
  // ══════════════════════════════════════════════════════════

  async saveDischargeSummary(summary: CachedDischargeSummary): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.DISCHARGE_STORE, 'readwrite');
      tx.objectStore(this.DISCHARGE_STORE).put(summary);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getAllDischargeSummaries(): Promise<CachedDischargeSummary[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.DISCHARGE_STORE, 'readonly');
      const request = tx.objectStore(this.DISCHARGE_STORE).getAll();
      // Only return summaries that are genuinely discharged (have checkOutTime)
      // Purge any accidental previews that got cached without a checkout
      request.onsuccess = () => {
        const all: CachedDischargeSummary[] = request.result || [];
        resolve(all.filter(s => !!s.checkOutTime && isFreshCacheRecord(s)));
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getDischargeSummary(visitId: number): Promise<CachedDischargeSummary | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.DISCHARGE_STORE, 'readonly');
      const request = tx.objectStore(this.DISCHARGE_STORE).get(`discharge_${visitId}`);
      request.onsuccess = () => resolve(request.result && isFreshCacheRecord(request.result) ? request.result : null);
      request.onerror = () => reject(request.error);
    });
  }

  // ══════════════════════════════════════════════════════════
  //  PAYMENT RECORDS — Append-only transaction log
  // ══════════════════════════════════════════════════════════

  async savePaymentRecord(record: CachedPaymentRecord): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.PAYMENT_STORE, 'readwrite');
      tx.objectStore(this.PAYMENT_STORE).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getAllPaymentRecords(): Promise<CachedPaymentRecord[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.PAYMENT_STORE, 'readonly');
      const request = tx.objectStore(this.PAYMENT_STORE).getAll();
      request.onsuccess = () => resolve(((request.result || []) as CachedPaymentRecord[]).filter(r => isFreshCacheRecord(r)));
      request.onerror = () => reject(request.error);
    });
  }

  async getPaymentsByVisit(visitId: number): Promise<CachedPaymentRecord[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.PAYMENT_STORE, 'readonly');
      const index = tx.objectStore(this.PAYMENT_STORE).index('visitId');
      const request = index.getAll(visitId);
      request.onsuccess = () => resolve(((request.result || []) as CachedPaymentRecord[]).filter(r => isFreshCacheRecord(r)));
      request.onerror = () => reject(request.error);
    });
  }

  // ══════════════════════════════════════════════════════════
  //  CLEANUP — Remove records past their lifetime (B7: 24 h)
  // ══════════════════════════════════════════════════════════

  /** Deletes every cached record older than maxAgeMs (default OFFLINE_CACHE_MAX_AGE_MS) from disk. Resolves once the
   *  deletions are committed (the old version returned before its cursors finished, and nothing ever called it). */
  async cleanupOldRecords(maxAgeMs: number = OFFLINE_CACHE_MAX_AGE_MS): Promise<void> {
    const now = Date.now();
    let db: IDBDatabase;
    try { db = await this.getDB(); } catch { return; }

    for (const storeName of [this.BILLING_STORE, this.DISCHARGE_STORE, this.PAYMENT_STORE]) {
      await new Promise<void>((resolve) => {
        let tx: IDBTransaction;
        try { tx = db.transaction(storeName, 'readwrite'); } catch { resolve(); return; }
        const request = tx.objectStore(storeName).openCursor();
        request.onsuccess = (event: any) => {
          const cursor = event.target.result;
          if (!cursor) return;
          if (!isFreshCacheRecord(cursor.value, now, maxAgeMs)) cursor.delete();
          cursor.continue();
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
      });
    }
  }

  // ══════════════════════════════════════════════════════════
  //  SIGN-OUT WIPE — DISC-038 (shared-PC PHI)
  // ══════════════════════════════════════════════════════════

  /**
   * DISC-038 / TC-5494: wipe every cached record on sign-out so one person's billing, discharge
   * and payment data (patient names, phones, diagnoses, prescriptions, amounts) can never be read
   * by the next person on a shared device. Clears EVERY object store in the database — not just the
   * three known names — so any store a future version adds is wiped too. Never throws; resolves once
   * the stores are empty (or immediately when there is nothing to clear or the DB will not open).
   */
  async clearAllData(): Promise<void> {
    // Stop any running sync first, so it cannot repopulate the stores we are about to empty.
    if (this.syncInterval) { try { clearInterval(this.syncInterval); } catch { /* ignore */ } this.syncInterval = null; }

    let db: IDBDatabase;
    try { db = await this.getDB(); } catch { return; }

    const stores = Array.from(db.objectStoreNames);
    if (stores.length === 0) { this.lastSyncTime$.next(0); return; }

    await new Promise<void>((resolve) => {
      let tx: IDBTransaction;
      try { tx = db.transaction(stores, 'readwrite'); }
      catch { resolve(); return; }
      for (const name of stores) { try { tx.objectStore(name).clear(); } catch { /* ignore */ } }
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });

    this.lastSyncTime$.next(0);
  }

  // ══════════════════════════════════════════════════════════
  //  STATS — For emergency dashboard header
  // ══════════════════════════════════════════════════════════

  async getCacheStats(): Promise<{
    billingCount: number;
    dischargeCount: number;
    paymentCount: number;
    totalPending: number;
    totalCollected: number;
    lastSyncedAt: number;
  }> {
    const billing = await this.getAllBillingRecords();
    const payments = await this.getAllPaymentRecords();
    const discharges = await this.getAllDischargeSummaries();

    const totalPending = billing.reduce((sum, b) => sum + (b.totalPending || 0), 0);
    const totalCollected = billing.reduce((sum, b) => sum + (b.totalPaid || 0), 0);
    const lastSynced = billing.length > 0
      ? Math.max(...billing.map(b => b.lastSyncedAt))
      : 0;

    return {
      billingCount: billing.length,
      dischargeCount: discharges.length,
      paymentCount: payments.length,
      totalPending,
      totalCollected,
      lastSyncedAt: lastSynced
    };
  }

  // EXPORT — Removed 2026-10-20: the CSV-export helpers (exportBillingCSV / exportDischargeCSV / csvCell)
  // only fed the dead DailyExportService, which auto-downloaded patient bills + discharge summaries to the
  // local Downloads folder on an 8-hour timer (a shared-PC PHI risk) and was never wired up. Both the service
  // and these orphaned PHI-CSV builders are deleted. If shift-handover export is wanted later, route it through
  // the authenticated, audited server export path (like the payment-audit CSV), not a client-side auto-download.
}
